import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    DatetimePicker,
    DynamicInput,
    FormLabel,
    IValidateError,
    OptionType,
    SearchableSelect,
    uniqueStringList,
} from "shared";
import {
    IBankDetail,
    IBusinessGroup,
    IOrganization,
    IOrgBankDetail,
    ITemplateYandexComponent,
    templatesService,
    YandexListTypes
} from "entities/template";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { YandexDocumentSelectField } from "../../../../lib/const/yandex_document_select_fields";
import { YandexWordFields } from "../../../../lib/const/yandex_word_fields";
import { yandexBankFields, yandexOrganizationFields } from "../../../../lib/utils/field_list";
import { constructOptions } from "../../../../lib/utils/dynamic_document_form_utils";
import { ExternalUpdateMode } from "../../../../lib/types/share-state";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import { cleanUndefinedNullObject } from "../../../../lib/utils/cleanupExternalRecord";
import styles from "./document_form_yandex.module.scss";

interface IDocumentFormYandexProps {
    component: ITemplateYandexComponent;
    organizations: IOrganization[];
    businessGroups: IBusinessGroup[];
    externalUpdateRecord: Record<string, any>;
    onChange: (partial: Record<string, any>) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    isExternalProtected: boolean;
    triggerValidate: number;
    externalUpdateMode: ExternalUpdateMode;
}

export const DocumentFormYandex: React.FC<IDocumentFormYandexProps> = React.memo(({
                                                                                      component,
                                                                                      organizations,
                                                                                      businessGroups,
                                                                                      label,
                                                                                      triggerValidate,
                                                                                      tooltip,
                                                                                      readonly,
                                                                                      isExternalProtected,
                                                                                      externalUpdateRecord,
                                                                                      externalUpdateMode,
                                                                                      onValidationRegister,
                                                                                      onChange,
                                                                                  }) => {
    const [inn, setInn] = useState<string>("");
    const [record, setRecord] = useState<Record<string, string | undefined>>({});
    const [bankDetails, setBankDetails] = useState<IOrgBankDetail[]>([]);
    const [companyName, setCompanyName] = useState<OptionType>();
    const [companyNameOptions, setCompanyNameOptions] = useState<OptionType[]>([]);
    const [bankName, setBankName] = useState<OptionType>();
    const [bankNameOptions, setBankNameOptions] = useState<OptionType[]>([]);
    const [bic, setBic] = useState<OptionType>();
    const [bicOptions, setBicOptions] = useState<OptionType[]>([]);
    const [paymentAccount, setPaymentAccount] = useState<OptionType>();
    const [paymentAccountOptions, setPaymentAccountOptions] = useState<OptionType[]>([]);
    const [corrAccountOptions, setCorrAccountOptions] = useState<OptionType[]>([]);
    const [corrAccount, setCorrAccount] = useState<OptionType>();
    const [companyFullNameOptions, setCompanyFullNameOptions] = useState<OptionType[]>([]);
    const [companyFullName, setCompanyFullName] = useState<OptionType>();
    const [useExternalValues, setUseExternalValues] = useState<boolean>(false);
    const { i18n } = useTranslation();
    const t = useComponentsTranslations();
    const { extUserId } = useParams();

    const componentSuffix = useMemo(() => {
        return component.suffix ? component.suffix : "";
    }, [component.suffix])

    const selectFields = useMemo(() => Object.values(YandexDocumentSelectField) as string[], []);
    const selectVariablesMapping: Record<YandexDocumentSelectField, any> = {
        [YandexDocumentSelectField.ya_name]: [companyName, setCompanyName, companyNameOptions],
        [YandexDocumentSelectField.ya_full_name]: [companyFullName, setCompanyFullName, companyFullNameOptions],
        [YandexDocumentSelectField.ya_bank_name]: [bankName, setBankName, bankNameOptions],
        [YandexDocumentSelectField.ya_bic]: [bic, setBic, bicOptions],
        [YandexDocumentSelectField.ya_payment_account]: [paymentAccount, setPaymentAccount, paymentAccountOptions],
        [YandexDocumentSelectField.ya_corr_account]: [corrAccount, setCorrAccount, corrAccountOptions],
    }

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        }
    }, []);

    useEffect(() => {
        return () => {
            const recordChanges: Record<string, any> = {};
            const suffix = component.suffix ? component.suffix : "";
            onValidationRegister(() => {
                return Promise.resolve(true)
            }, `yandex_${suffix}`);

            [...yandexOrganizationFields, ...yandexBankFields].forEach((key) => {
                recordChanges[`${key}${suffix}`] = undefined;
            })
            onChange(recordChanges);
        }
    }, [])

    useEffect(() => {
        const recordChanges: Record<string, any> = {};
        const suffix = component.suffix ? component.suffix : "";
        [...yandexOrganizationFields, ...yandexBankFields].forEach((key) => {
            recordChanges[`${key}${suffix}`] = record[key] ? record[key] : undefined;
        })
        onChange(recordChanges);
    }, [record, component.suffix])

    useEffect(() => {
        const orgNames = companyNameOptions.map(o => o.value);
        const orgByInn = organizations.filter(o => o.ya_inn === inn && orgNames.includes(o.ya_name));
        if (inn && orgByInn.length > 0) {
            const companyName = companyNameOptions.find(o => o.value === orgByInn[0].ya_name)
            setCompanyName(companyName);
        }
    }, [inn])

    useEffect(() => {
        if (companyNameOptions.length > 0) {
            const suffix = component.suffix ? component.suffix : "";
            const companyFullName = externalUpdateRecord[`${YandexDocumentSelectField.ya_full_name}${suffix}`];
            const companyName = externalUpdateRecord[`${YandexDocumentSelectField.ya_name}${suffix}`];
            if (!companyName && !companyFullName) {
                if (component.defaultValue) {
                    setCompanyName(constructOptions([component.defaultValue])[0]);
                }
            }
        }
    }, [companyNameOptions])

    useEffect(() => {
        const suffix = component.suffix ? component.suffix : "";
        const companyFullName = externalUpdateRecord[`${YandexDocumentSelectField.ya_full_name}${suffix}`];
        const companyName = externalUpdateRecord[`${YandexDocumentSelectField.ya_name}${suffix}`];
        if (companyFullName) {
            setCompanyFullName(companyFullNameOptions.find(o => o.value === companyFullName))
            setUseExternalValues(true);
        } else {
            if (companyName) {
                setCompanyName(companyNameOptions.find(o => o.value === companyName))
                setUseExternalValues(true);
            } else if (externalUpdateMode === "update") {
                clearForm();
            }
        }
    }, [externalUpdateRecord, externalUpdateMode, component.suffix, component.fieldsList, companyFullNameOptions, companyNameOptions])

    useEffect(() => {
        if (companyName && companyName.value) {
            const cFullName = organizations.find((o) => o.ya_name === companyName.value)?.ya_full_name;
            updateByCompanyName(companyName.value);
            if (cFullName && cFullName !== companyFullName?.value) {
                setCompanyFullName(companyFullNameOptions.find(o => o.value === cFullName))
            }
        } else {
            clearForm();
        }
    }, [companyName]);

    useEffect(() => {
        if (companyFullName && companyFullName.value) {
            const cName = organizations.find((o) => o.ya_full_name === companyFullName.value)?.ya_name;
            if (cName && cName !== companyName?.value) {
                setCompanyName(companyNameOptions.find(o => o.value === cName))
            }
        } else {
            clearForm();
        }
    }, [companyFullName]);

    useEffect(() => {
        if (bankName && bankDetails.length > 0) {
            const bankBic = bankDetails.find(detail => detail.ya_bank_name === bankName.value)?.ya_bic;
            if (bankBic && bic?.value !== bankBic) {
                setBic(constructOptions([bankBic])[0])
            }
        }
        updateRecordWithObject({ ya_bank_name: bankName?.value }, ["ya_bank_name"])
    }, [bankName, bankDetails])

    useEffect(() => {
        if (bic && bankDetails.length > 0) {
            const paymentAccountOpts = new Set<string>();
            const corrAccountOpts = new Set<string>();
            const filteredBankDetails = bankDetails.filter(detail => detail.ya_bic === bic.value);
            filteredBankDetails.forEach((detail) => {
                paymentAccountOpts.add(detail.ya_payment_account);
                corrAccountOpts.add(detail.ya_corr_account);
            })
            const corrAccountOptions = constructOptions(Array.from(corrAccountOpts));
            const payAccountOptions = constructOptions(Array.from(paymentAccountOpts));
            setCorrAccountOptions(corrAccountOptions);
            setPaymentAccountOptions(payAccountOptions);
            if (!paymentAccount?.value || !payAccountOptions.map(o => o.value).includes(paymentAccount.value)) {
                setPaymentAccount(payAccountOptions[0]);
            }
            if (!corrAccount?.value || !corrAccountOptions.map(o => o.value).includes(corrAccount.value)) {
                setCorrAccount(corrAccountOptions[0]);
            }
            const bName = bankDetails.find(detail => detail.ya_bic === bic.value)?.ya_bank_name;
            if (bName && bankName?.value !== bName) {
                setBankName(constructOptions([bName])[0])
            }
        }
        updateRecordWithObject({ ya_bic: bic?.value }, ["ya_bic"])
    }, [bic, bankDetails])

    useEffect(() => {
        updateRecordWithObject({ ya_payment_account: paymentAccount?.value }, ["ya_payment_account"])
    }, [paymentAccount])

    useEffect(() => {
        updateRecordWithObject({ ya_corr_account: corrAccount?.value }, ["ya_corr_account"])
    }, [corrAccount])

    const updateByCompanyName = (c: string) => {
        templatesService.getOrganizationDetails(c, extUserId).then((org) => {
            if (org) {
                const orgInn = org.ya_inn;
                setInn(orgInn ? orgInn : "");
                updateRecordWithObject(org as Record<string, any>, yandexOrganizationFields);
            } else {
                setRecord({ [YandexDocumentSelectField.ya_name]: c })
            }
            if (org?.ya_bank_details && org.ya_bank_details.length > 0 && component.listBankDetails) {
                const companyBankDetails = component.listBankDetails?.find((d) => d.item === c);
                if (companyBankDetails && companyBankDetails.bankNameWhiteList.length > 0) {
                    updateBankDetails(org.ya_bank_details, companyBankDetails)
                } else {
                    clearBankDetails();
                }
            } else {
                clearBankDetails();
            }
            if (useExternalValues) {
                setUseExternalValues(false);
                setTimeout(() => {
                    const updateChanges: Record<string, any> = {};
                    const suffix = component.suffix ? component.suffix : "";
                    yandexOrganizationFields.forEach((key) => {
                        if ([YandexDocumentSelectField.ya_bank_name,
                            YandexDocumentSelectField.ya_bic,
                            YandexDocumentSelectField.ya_payment_account,
                            YandexDocumentSelectField.ya_corr_account
                        ].includes(key as YandexDocumentSelectField)) {
                            const option = externalUpdateRecord[`${key}${suffix}`] ? {
                                value: externalUpdateRecord[`${key}${suffix}`],
                                label: externalUpdateRecord[`${key}${suffix}`]
                            } : undefined;

                            const [value, setValue, options] = selectVariablesMapping[key as YandexDocumentSelectField];
                            if (option || externalUpdateMode === "update") {
                                setValue(option);
                            }
                        }
                        updateChanges[key] = externalUpdateRecord[`${key}${suffix}`] ? externalUpdateRecord[`${key}${suffix}`] : undefined;
                    })
                    setRecord((prevState) => {
                        if (externalUpdateMode === "update") {
                            return updateChanges;
                        } else {
                            return {...prevState, ...cleanUndefinedNullObject(updateChanges)}
                        }
                    });
                }, 200)
            }
        })
    }

    const clearBankDetails = () => {
        setBankDetails([]);
        setBankName(undefined);
        setBankNameOptions([]);
        setBicOptions([]);
        setBic(undefined);
        setPaymentAccountOptions([]);
        setPaymentAccount(undefined);
        setCorrAccountOptions([]);
        setCorrAccount(undefined);
    }

    const updateBankDetails = (orgBankDetails: IOrgBankDetail[], companyBankDetails: IBankDetail) => {
        const filteredBankDetails = orgBankDetails.filter(detail => companyBankDetails.bankNameWhiteList.includes(detail.id));
        setBankDetails(filteredBankDetails);
        const bankNameOpts = new Set<string>();
        const bicOpts = new Set<string>();
        filteredBankDetails.forEach((detail) => {
            bankNameOpts.add(detail.ya_bank_name);
            bicOpts.add(detail.ya_bic);
        })
        const uniqueBankOptions = constructOptions(Array.from(bankNameOpts));
        const uniqueBicOptions = constructOptions(Array.from(bicOpts));
        setBankNameOptions(uniqueBankOptions);
        setBicOptions(uniqueBicOptions);
        if (companyBankDetails.bankNameDefaultValue) {
            const defaultBankValue = filteredBankDetails.find(detail => detail.id === companyBankDetails.bankNameDefaultValue);
            if (defaultBankValue) {
                setBankName(constructOptions([defaultBankValue.ya_bank_name])[0]);
                setBic(constructOptions([defaultBankValue.ya_bic])[0]);
                setCorrAccount(constructOptions([defaultBankValue.ya_corr_account])[0]);
                setPaymentAccount(constructOptions([defaultBankValue.ya_payment_account])[0]);
                updateRecordWithObject(defaultBankValue, yandexBankFields);
            }
        }
    }

    const handleClearEvent = () => {
        if (!isExternalProtected) {
            clearForm();
        }
    }

    const clearForm = () => {
        setInn("");
        setRecord({});
        setCompanyName(undefined);
        setCompanyFullName(undefined);
        clearBankDetails();
    }

    useEffect(() => {
        if (organizations.length > 0 && businessGroups.length > 0) {
            let companyList: string[];
            if (component.optionsList === YandexListTypes.yaBGs) {
                const businessGroupIds = businessGroups.filter((bg) => component.list?.includes(bg.name)).map(bg => bg.id);
                companyList = organizations.filter(c => businessGroupIds.includes(c.ya_bg_id)).map(c => c.ya_name);
                if (companyList.length === 0) {
                    // если нет компаний, то загружаем дефолтные
                    const businessGroupIds = [0];
                    companyList = organizations.filter(c => businessGroupIds.includes(c.ya_bg_id)).map(c => c.ya_name);
                }
            } else {
                const orgNameMap = organizations.map(o => o.ya_name);
                companyList = uniqueStringList(component.list ? component.list.filter(companyName => orgNameMap.includes(companyName)) : []);
                if (companyList.length === 0) {
                    // если нет компаний, то показываем дефолтные (ООО Яндекс)
                    companyList = organizations.filter((o) => o.isDefault === "1").map(c => c.ya_name);
                }
            }
            setCompanyNameOptions(constructOptions(companyList));
            const fullCompanyOptions = constructOptions(organizations.filter(o => companyList.includes(o.ya_name)).map(o => o.ya_full_name));
            setCompanyFullNameOptions(fullCompanyOptions);
        }
    }, [component.list, organizations, businessGroups])

    const required = useMemo(() => {
        return component.required !== false;
    }, [component])

    const updateRecordWithObject = (updateObj: Record<string, any> | undefined, fieldsToUpdate: string[]) => {
        const update: Record<string, string | undefined> = {};
        fieldsToUpdate.forEach((f) => {
            update[f] = updateObj ? updateObj[f] : undefined;
        })
        setRecord((prevState) => {
            return { ...prevState, ...update }
        })
    }

    const validateFields = (): boolean => {
        const yandexFields = Object.keys(YandexWordFields);
        return !!component.fieldsList?.every(field => {
            return !!record[field] || !yandexFields.includes(field)
        });
    }

    useEffect(() => {
        onValidationRegister(validateForm, `yandex_${component.suffix ? component.suffix : ""}`);
    }, [component.suffix, record, required])

    const onFieldChange = (field: string, value: string | undefined) => {
        setRecord((prevState) => {
            return { ...prevState, [field]: value }
        })
    }

    const validateForm = (): Promise<boolean> => {
        let valid = true;
        if (required) {
            valid = validateFields();
        }
        return Promise.resolve(valid);
    }

    const isDateField = (field: string): boolean => {
        return field.includes("date");
    }

    const isShowSelect = (field: string) => {
        const isSelectField = selectFields.includes(field);
        if (isSelectField) {
            const [value, setValue, options] = selectVariablesMapping[field as YandexDocumentSelectField]
            return options.length > 0;
        } else {
            return false;
        }
    }

    const validateFunction = useCallback((value: string): IValidateError => {
        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            }
        }
        return {
            valid: true
        }
    }, [component, i18n.language])

    const sortFieldList = (fieldList?: string[]): string[] => {
        const updatedFieldList: string[] = [];
        if (!fieldList) {
            return updatedFieldList;
        } else {
            Object.values(YandexWordFields).forEach((field) => {
                if (fieldList.includes(field)) {
                    updatedFieldList.push(field);
                }
            });

            return updatedFieldList;
        }
    }

    const renderSelect = (field: YandexDocumentSelectField) => {
        const [value, setValue, options] = selectVariablesMapping[field];
        return <SearchableSelect
            optionValue={value}
            validate={required}
            triggerValidate={triggerValidate}
            options={options}
            disabled={isExternalProtected}
            isMulti={false}
            isCreatable={!component.selectonly && ![YandexDocumentSelectField.ya_name, YandexDocumentSelectField.ya_full_name].includes(field)}
            isClearable={true}
            onOptionChange={(o) => setValue(o ? o as OptionType : undefined)}
        />
    }

    return <div className={styles.componentContainer}>
        <div className={styles.titleContainer}>
            <div className={styles.title}>{label}</div>
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltipContainer}/>
        </div>
        <div className={styles.container}>
            {(!component.fieldsList || (Array.isArray(component.fieldsList) && component.fieldsList.length === 0)) &&
                <div>
                    {t("fieldListEmpty")}
                </div>}
            {sortFieldList(component.fieldsList).map((field: string) =>
                <FormLabel
                    title={t(`yandexFields.${field as YandexWordFields}`)}
                    disabled={isExternalProtected || (readonly && !isShowSelect(field))}
                    key={field}
                    required={required}>
                    {isShowSelect(field) ? renderSelect(field as YandexDocumentSelectField) :
                        <>
                            {!isDateField(field) && <DynamicInput size="m"
                                                                  textareaEdge={70}
                                                                  name={field + componentSuffix}
                                                                  containerClassName={styles.input}
                                                                  validate={required}
                                                                  validateFunc={validateFunction}
                                                                  triggerValidate={triggerValidate}
                                                                  disabled={readonly}
                                                                  value={record[field] ? record[field] as string : ""}
                                                                  onChange={(e: any) => {
                                                                      if (field === YandexWordFields.ya_inn) {
                                                                          setInn(e.target.value ? e.target.value : "")
                                                                      }
                                                                      onFieldChange(field, e.target.value ? e.target.value : undefined)
                                                                  }}
                                                                  view="outline"/>}
                            {isDateField(field) && <DatetimePicker
                                validate={required}
                                className={styles.input}
                                triggerValidate={triggerValidate}
                                disabled={readonly || (selectFields.includes(field) && component.selectonly)}
                                initialValue={record[field] ? record[field] as string : null}
                                dateFormat="DD.MM.YYYY"
                                onUpdate={(date) => onFieldChange(field, date ? date : undefined)}
                                withTime={false}
                            />}
                        </>
                    }
                </FormLabel>)}
        </div>
    </div>;
});
